import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material'
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  UploadFile as ImportIcon,
} from '@mui/icons-material'
import { usePOTracking } from '@/hooks/usePOTracking'
import { OverviewChart } from '@/components/PoTracking/OverviewChart'
import { POTable } from '@/components/PoTracking/POTable'
import { POImportDialog } from '@/components/PoTracking/POImportDialog'
import type { ForecastConfidence, ForecastObjective, PurchaseOrderWithUsage } from '@/types/po'

interface KPI {
  label: string
  value: string
  color: 'primary' | 'success' | 'warning' | 'error' | 'default'
}

const ADD_YEAR_VALUE = -1
const lineTypeOptions = [
  { value: 'linear', label: 'Linear' },
  { value: 'monotone', label: 'Monotone' },
  { value: 'step', label: 'Step' },
  { value: 'stepAfter', label: 'Step After' },
] as const

type LineTypeOption = typeof lineTypeOptions[number]['value']
const FORECAST_OBJECTIVE: ForecastObjective = 'balanced'

function confidenceChipColor(confidence: ForecastConfidence): 'success' | 'warning' | 'error' {
  if (confidence === 'high') return 'success'
  if (confidence === 'medium') return 'warning'
  return 'error'
}

export default function PoTrackingPage() {
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [filteredPOId, setFilteredPOId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPO, setEditingPO] = useState<PurchaseOrderWithUsage | null>(null)
  const [customYears, setCustomYears] = useState<number[]>([])
  const [lineType, setLineType] = useState<LineTypeOption>('stepAfter')
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [addYear, setAddYear] = useState<{ open: boolean; onAdded?: (y: number) => void; value: string; error: string }>({
    open: false,
    value: '',
    error: '',
  })

  const [importOpen, setImportOpen] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importNotice, setImportNotice] = useState<string | null>(null)

  const { useYears, usePOsWithUsage, useForecast, createPO, updatePO, deletePO, importPOs } = usePOTracking()

  const { data: years = [] } = useYears()
  const { data: pos = [], isLoading: posLoading, refetch: refetchPOs } = usePOsWithUsage(selectedYear)
  const { data: forecast, isLoading: forecastLoading } = useForecast(
    selectedYear,
    filteredPOId ?? undefined,
    FORECAST_OBJECTIVE
  )

  // Calculate KPIs
  const kpis: KPI[] = useMemo(() => {
    const totalPot = pos.reduce((sum, po) => sum + po.valueTotalEur, 0)
    const totalUsed = pos.reduce((sum, po) => sum + po.valueUsed, 0)
    const totalRemaining = totalPot - totalUsed
    const totalOverrun = pos.reduce((sum, po) => sum + Math.max(0, po.valueUsed - po.valueTotalEur), 0)

    const formatEUR = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)

    return [
      { label: 'Total PO Budget', value: formatEUR(totalPot), color: 'primary' as const },
      { label: 'Total Used', value: formatEUR(totalUsed), color: 'success' as const },
      { label: 'Remaining', value: formatEUR(Math.max(0, totalRemaining)), color: totalOverrun > 0 ? 'warning' as const : 'default' as const },
      { label: 'Overrun', value: formatEUR(totalOverrun), color: totalOverrun > 0 ? 'error' as const : 'default' as const },
    ]
  }, [pos])

  const handleCreatePO = () => {
    setEditingPO(null)
    setDialogOpen(true)
  }

  const handleEditPO = (po: PurchaseOrderWithUsage) => {
    setEditingPO(po)
    setDialogOpen(true)
  }

  const handleDeletePO = (id: string) => setDeleteTargetId(id)

  const confirmDeletePO = async () => {
    if (!deleteTargetId) return
    await deletePO.mutateAsync(deleteTargetId)
    setDeleteTargetId(null)
    refetchPOs()
  }

  const handleSavePO = async (data: { poNumber: string; description: string | null; valueTotalEur: number; year: number }) => {
    if (editingPO) {
      await updatePO.mutateAsync({ id: editingPO.id, data })
    } else {
      await createPO.mutateAsync(data)
    }
    setDialogOpen(false)
    refetchPOs()
  }

  const handleFilterByPO = (poId: string | null) => {
    setFilteredPOId(poId)
  }

  const handleImportPOs = async (file: File, year: number, mode: 'update' | 'replace') => {
    setImportError(null)
    try {
      const result = await importPOs.mutateAsync({ file, year, mode })
      setImportOpen(false)
      setSelectedYear(year)
      refetchPOs()
      const unmatched = result.unmatchedSuppliers
      const summary =
        result.mode === 'replace'
          ? `Replaced ${year} with ${result.imported} PO${result.imported !== 1 ? 's' : ''}.`
          : `${year}: ${result.updated} updated, ${result.created} added.`
      setImportNotice(
        summary +
          (unmatched.length
            ? ` ${unmatched.length} supplier(s) not in the whitelist: ${unmatched.join(', ')}`
            : '')
      )
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Import failed')
    }
  }

  const currentYear = new Date().getFullYear()

  // Add current year to list if not present
  const availableYears = useMemo(() => {
    const yearSet = new Set(years)
    yearSet.add(currentYear)
    customYears.forEach((year) => yearSet.add(year))
    return Array.from(yearSet).sort((a, b) => b - a)
  }, [years, currentYear, customYears])

  const openAddYear = (onAdded?: (year: number) => void) => {
    setAddYear({ open: true, onAdded, value: '', error: '' })
  }

  const confirmAddYear = () => {
    const parsed = Number(addYear.value)
    if (!Number.isInteger(parsed) || parsed < 2000 || parsed > 2100) {
      setAddYear((s) => ({ ...s, error: 'Please enter a valid year (2000–2100)' }))
      return
    }
    setCustomYears((prev) => (prev.includes(parsed) ? prev : [...prev, parsed]))
    setSelectedYear(parsed)
    addYear.onAdded?.(parsed)
    setAddYear({ open: false, value: '', error: '' })
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          PO Tracking
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<ImportIcon />} onClick={() => setImportOpen(true)}>
            Import PO List
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreatePO}>
            New PO
          </Button>
        </Stack>
      </Box>

      {/* Year Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1">Year:</Typography>
          <Select
            value={selectedYear}
            onChange={(e) => {
              const value = Number(e.target.value)
              if (value === ADD_YEAR_VALUE) {
                openAddYear()
                return
              }
              setSelectedYear(value)
            }}
            sx={{ minWidth: 120 }}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
            <MenuItem value={ADD_YEAR_VALUE}>
              + Add New Year
            </MenuItem>
          </Select>

          {filteredPOId && (
            <Chip
              label="Filtered by PO"
              onDelete={() => setFilteredPOId(null)}
              color="primary"
              deleteIcon={<TrendingUpIcon />}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="textSecondary">
            {pos.length} PO{pos.length !== 1 ? 's' : ''} in {selectedYear}
          </Typography>
        </Stack>
      </Paper>

      {/* KPI Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} useFlexGap>
        {kpis.map((kpi) => (
          <Paper key={kpi.label} sx={{ flex: 1, p: 2, minWidth: 200 }}>
            <Typography variant="caption" color="textSecondary">
              {kpi.label}
            </Typography>
            <Typography variant="h6" color={kpi.color}>
              {kpi.value}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {/* Overview and Forecast Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6">
            Overview and Forecast
            {filteredPOId && ` (Filtered)`}
          </Typography>
          {forecast && (
            <Chip
              size="small"
              color={confidenceChipColor(forecast.forecastMeta.confidence)}
              variant="outlined"
              label={`Forecast confidence: ${forecast.forecastMeta.confidence}`}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="line-type-label">Line</InputLabel>
            <Select
              labelId="line-type-label"
              value={lineType}
              label="Line"
              onChange={(e) => setLineType(e.target.value as LineTypeOption)}
            >
              {lineTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {forecastLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : forecast ? (
          <OverviewChart data={forecast} lineType={lineType} />
        ) : (
          <Alert severity="info">No forecast data available for the selected period.</Alert>
        )}
      </Paper>

      {/* PO Table */}
      <POTable
        pos={pos}
        loading={posLoading}
        onEdit={handleEditPO}
        onDelete={handleDeletePO}
        onFilter={handleFilterByPO}
        filteredPOId={filteredPOId}
      />

      {/* Create/Edit Dialog */}
      <PODialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSavePO}
        po={editingPO}
        availableYears={availableYears}
        onAddYear={openAddYear}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteTargetId !== null} onClose={() => setDeleteTargetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete PO?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes the purchase order. Linked invoices will be unassigned (not deleted).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetId(null)}>Cancel</Button>
          <Button onClick={confirmDeletePO} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Add year */}
      <Dialog open={addYear.open} onClose={() => setAddYear({ open: false, value: '', error: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Add Year</DialogTitle>
        <DialogContent>
          <TextField
            label="Year"
            type="number"
            value={addYear.value}
            onChange={(e) => setAddYear((s) => ({ ...s, value: e.target.value, error: '' }))}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmAddYear() }}
            error={!!addYear.error}
            helperText={addYear.error || 'e.g. 2027'}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddYear({ open: false, value: '', error: '' })}>Cancel</Button>
          <Button onClick={confirmAddYear} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Import PO list */}
      <POImportDialog
        open={importOpen}
        defaultYear={selectedYear}
        loading={importPOs.isPending}
        error={importError}
        onClose={() => {
          setImportOpen(false)
          setImportError(null)
        }}
        onImport={handleImportPOs}
      />

      {/* Import result notice */}
      <Snackbar
        open={!!importNotice}
        autoHideDuration={8000}
        onClose={() => setImportNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setImportNotice(null)} severity="success" sx={{ width: '100%' }}>
          {importNotice}
        </Alert>
      </Snackbar>
    </Box>
  )
}

interface PODialogProps {
  open: boolean
  onClose: () => void
  onSave: (data: { poNumber: string; description: string | null; valueTotalEur: number; year: number }) => void
  po: PurchaseOrderWithUsage | null
  availableYears: number[]
  onAddYear: (onAdded?: (year: number) => void) => void
}

function PODialog({ open, onClose, onSave, po, availableYears, onAddYear }: PODialogProps) {
  const [poNumber, setPONumber] = useState('')
  const [description, setDescription] = useState('')
  const [valueTotalEur, setValueTotalEur] = useState('')
  const [year, setYear] = useState<number>(new Date().getFullYear())
  const [error, setError] = useState('')

  // Reset form when PO changes or dialog opens
  useEffect(() => {
    if (open) {
      setError('')
      if (po) {
        setPONumber(po.poNumber)
        setDescription(po.description || '')
        setValueTotalEur(po.valueTotalEur.toString())
        setYear(po.year)
      } else {
        setPONumber('')
        setDescription('')
        setValueTotalEur('')
        setYear(new Date().getFullYear())
      }
    }
  }, [open, po])

  const handleSave = () => {
    const value = parseFloat(valueTotalEur)
    if (!poNumber.trim()) {
      setError('PO Number is required')
      return
    }
    if (isNaN(value) || value < 0) {
      setError('Please enter a valid amount')
      return
    }
    setError('')

    onSave({
      poNumber: poNumber.trim(),
      description: description.trim() || null,
      valueTotalEur: value,
      year,
    })

    // Reset form
    setPONumber('')
    setDescription('')
    setValueTotalEur('')
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{po ? 'Edit PO' : 'Create New PO'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="PO Number"
            value={poNumber}
            onChange={(e) => setPONumber(e.target.value)}
            fullWidth
            autoFocus
            placeholder="e.g. PO-2024-001"
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Optional description"
          />

          <TextField
            label="Total Value (EUR)"
            value={valueTotalEur}
            onChange={(e) => setValueTotalEur(e.target.value)}
            fullWidth
            type="number"
            InputProps={{
              startAdornment: <Box sx={{ mr: 1 }}>€</Box>,
            }}
          />

          <FormControl fullWidth>
            <InputLabel>Year</InputLabel>
            <Select
              value={year}
              onChange={(e) => {
                const value = Number(e.target.value)
                if (value === ADD_YEAR_VALUE) {
                  onAddYear((newYear) => setYear(newYear))
                  return
                }
                setYear(value)
              }}
              label="Year"
            >
              {availableYears.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
              <MenuItem value={ADD_YEAR_VALUE}>
                + Add New Year
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {po ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
